import { TicketingSystemField } from 'containers/ReportView/shared/adaptors/ticketing/createViewableSystemFields';
import type { ChartSort, ChartPeriod } from '../Widgets/types';
import {
  ChartFormula,
  type DatatableField,
  type ChartDisplay,
  type ChartXAxis,
  type ChartYAxis,
  ChartDisplayOption,
  ChartTypes,
} from '../Widgets/types';
import type {
  APIMeasureConfig,
  APIVisualization,
  APIVisualizationGroupBy,
  APIVisualizationMeasure,
  APIVisualizationSort,
} from '../_api/types/api_visualization';
import type { ReportingEngineWidgetSlice } from '../slices/useReportingEngineWidgetSlice';

export const getChartVisualization = (
  state: ReportingEngineWidgetSlice
): APIVisualization | null => {
  const {
    selectedChartType: widgetType,
    allFields,
    chartXAxis: lineOrBarChartXAxis,
    chartYAxis: lineOrBarChartYAxis,
    chartDisplay: lineOrBarChartDisplay,
    barChartDisplay: barPosition,
    metricCurrencySymbol,
    metricDecimalsDisplay,
    metricFormattingDisplay,
    metricShowThousandSeparator,
  } = state;
  const defaultTicketId = allFields.find(
    (field) => field.originalName === TicketingSystemField.AccountTicketId
  );
  if (!defaultTicketId) {
    // What we need hasn't loaded yet
    return null;
  }

  const metricConfig: APIMeasureConfig = {
    currencySymbol: metricCurrencySymbol,
    decimal: metricDecimalsDisplay,
    format: metricFormattingDisplay,
    showThousandSeparator: metricShowThousandSeparator,
  };

  const columns: APIVisualizationGroupBy[] = [];
  for (const xAxis of lineOrBarChartXAxis) {
    const field = allFields.find((f) => f.id === xAxis.id);

    if (field) {
      columns.push({
        column: {
          label: field.label,
          ticketFieldId: field.id,
        },
        ...(xAxis.period && { period: xAxis.period }),
      });
    }
  }

  const measure: APIVisualizationMeasure = {
    column: {
      label: defaultTicketId.label || 'Ticket ID',
      ticketFieldId: defaultTicketId.id,
    },
    formula: lineOrBarChartYAxis.formula,
  };

  if (
    lineOrBarChartYAxis.measure !== ChartFormula.COUNT &&
    widgetType !== ChartTypes.METRIC
  ) {
    const field = allFields.find((f) => f.id === lineOrBarChartYAxis.measure);
    if (field) {
      measure.column = {
        label: field.label,
        ticketFieldId: field.id,
      };
    }
  } else if (widgetType === ChartTypes.METRIC && columns.length > 0) {
    let comparator = '';
    const firstColumnAsMeasure = columns[0].column;

    comparator = firstColumnAsMeasure.ticketFieldId ?? '';

    measure.column = {
      label: firstColumnAsMeasure.label,
      ticketFieldId: comparator,
    };
  }

  if (widgetType === ChartTypes.METRIC && metricConfig) {
    return {
      measure,
      measureConfig: {
        ...metricConfig,
        decimal: Number(metricConfig?.decimal),
      },
      groupBy: columns,
    };
  }

  const sort: APIVisualizationSort = {
    axis: lineOrBarChartDisplay.sort ?? 'x',
    order: lineOrBarChartDisplay.order ?? 'asc',
  };

  return {
    groupBy: columns,
    measure,
    sort,
    resultDisplayLimit:
      lineOrBarChartDisplay.displayOption === ChartDisplayOption.ALL
        ? undefined
        : Number(lineOrBarChartDisplay.displayOption),
    ...(widgetType === ChartTypes.BAR && { barPosition }),
  };
};

export const fromLineVisualizationToZustand = (
  allFields: DatatableField[],
  visualization: APIVisualization
): {
  lineOrBarChartXAxis: ChartXAxis[];
  lineOrBarChartYAxis: ChartYAxis;
  lineOrBarChartDisplay: ChartDisplay;
  barChartDisplay?: string;
  metricConfig?: APIVisualization['measureConfig'];
} => {
  const lineOrBarChartXAxis: ChartXAxis[] = [];
  if (visualization.groupBy) {
    for (const xAxis of visualization.groupBy) {
      const selectedField = allFields.find((perColumn) => {
        if (xAxis.column.ticketFieldId) {
          return perColumn.id === xAxis.column.ticketFieldId;
        }
        return false;
      });

      if (selectedField) {
        lineOrBarChartXAxis.push({
          id: selectedField.id,
          type: selectedField.type,
          period: xAxis.period || null,
        });
      }
    }
  }

  const measureColumn = visualization.measure.column;
  const measureField = allFields.find((perColumn) => {
    if (measureColumn.ticketFieldId) {
      return perColumn.id === measureColumn.ticketFieldId;
    }
    return false;
  });

  let measureId = String(ChartFormula.COUNT);
  if (
    measureField &&
    (visualization.measure.formula as ChartFormula) !== ChartFormula.COUNT
  ) {
    measureId = measureField.id;
  }

  const lineOrBarChartYAxis: ChartYAxis = {
    measure: measureId,
    formula: visualization.measure.formula as ChartFormula,
  };

  const getDisplayOption = (): ChartDisplayOption => {
    if (!visualization.resultDisplayLimit) {
      return ChartDisplayOption.ALL;
    }

    // @ts-ignore TS7053
    return ChartDisplayOption[`MAX_${visualization.resultDisplayLimit}`];
  };

  const lineOrBarChartDisplay: ChartDisplay = {
    sort: visualization.sort
      ? (visualization.sort.axis as ChartSort)
      : undefined,
    order: visualization.sort
      ? (visualization.sort.order as 'asc' | 'desc')
      : undefined,
    displayOption: getDisplayOption(),
  };

  return {
    lineOrBarChartXAxis,
    lineOrBarChartYAxis,
    lineOrBarChartDisplay,
    barChartDisplay: visualization.barPosition,
  };
};

export const fromMetricVisualizationToZustand = (
  allFields: DatatableField[],
  visualization: APIVisualization
): {
  lineOrBarChartXAxis: ChartXAxis[];
  lineOrBarChartYAxis: ChartYAxis;
  metricConfig?: APIVisualization['measureConfig'];
} => {
  const lineOrBarChartXAxis: ChartXAxis[] = [];
  if (visualization.measure) {
    const selectedField = allFields.find((perColumn) => {
      if (visualization.measure.column.ticketFieldId) {
        return perColumn.id === visualization.measure.column.ticketFieldId;
      }
      return false;
    });

    let period: ChartPeriod | null = null;
    if (visualization.groupBy) {
      period =
        visualization.groupBy.find(
          (g) =>
            g.column.ticketFieldId ===
            visualization.measure.column.ticketFieldId
        )?.period ?? null;
    }

    if (selectedField) {
      lineOrBarChartXAxis.push({
        id: selectedField.id,
        type: selectedField.type,
        period,
      });
    }
  }

  const { column: vMeasureColumn, formula: vMeasureFormula } =
    visualization.measure;
  const measure = allFields.find((perColumn) => {
    if (vMeasureColumn.ticketFieldId) {
      return perColumn.id === vMeasureColumn.ticketFieldId;
    }
    return false;
  });

  const measureId =
    vMeasureFormula !== ChartFormula.COUNT
      ? measure?.id
      : String(ChartFormula.COUNT);

  const lineOrBarChartYAxis: ChartYAxis = {
    measure: measureId ?? String(ChartFormula.COUNT),
    formula: vMeasureFormula as ChartFormula,
  };

  return {
    lineOrBarChartXAxis,
    lineOrBarChartYAxis,
    metricConfig: visualization.measureConfig,
  };
};
